import type { PollDto } from "../lib/api";
import { percent } from "../lib/format";

/** Accessible horizontal bar chart with a numeric fallback table. */
export default function ResultsCharts({ poll }: { poll: PollDto }) {
  const options = poll.options.filter((o) => o.published);
  const maxCount = Math.max(1, ...options.map((o) => o.count ?? 0));

  return (
    <div className="mt-6">
      <h4 className="text-sm font-semibold uppercase tracking-wider text-wandor-muted">
        Current results
      </h4>
      <ul className="mt-3 space-y-3">
        {options.map((o) => {
          const count = o.count ?? 0;
          const pct = o.percentage ?? 0;
          return (
            <li key={o.id}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-wandor-text">{o.label}</span>
                <span className="text-sm tabular-nums text-wandor-muted">
                  {count} vote{count === 1 ? "" : "s"} · {percent(pct)}
                </span>
              </div>
              <div
                className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-white/10"
                role="img"
                aria-label={`${o.label}: ${count} votes, ${percent(pct)}`}
              >
                <div
                  className="h-full rounded-full bg-wandor-prompt transition-[width] duration-500"
                  style={{ width: `${Math.round((count / maxCount) * 100)}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <table className="mt-4 w-full text-left text-xs">
        <caption className="sr-only">Numerical results for {poll.title}</caption>
        <thead>
          <tr className="border-b border-white/10 text-wandor-muted">
            <th scope="col" className="py-1.5 font-medium">Option</th>
            <th scope="col" className="py-1.5 font-medium">Votes</th>
            <th scope="col" className="py-1.5 font-medium">Share</th>
          </tr>
        </thead>
        <tbody>
          {options.map((o) => (
            <tr key={o.id} className="border-b border-white/5">
              <td className="py-1.5 text-wandor-text">{o.label}</td>
              <td className="py-1.5 tabular-nums text-wandor-text">{o.count ?? 0}</td>
              <td className="py-1.5 tabular-nums text-wandor-text">{percent(o.percentage ?? 0)}</td>
            </tr>
          ))}
          <tr>
            <td className="py-1.5 font-medium text-wandor-text">Ballots cast</td>
            <td className="py-1.5 tabular-nums font-medium text-wandor-text">{poll.ballotsCast}</td>
            <td className="py-1.5 text-wandor-muted">100%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
